import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import { AuthProvider, useAuth, authApi, WS_URL } from './auth';
import { changePassword } from './api';
import Login        from './pages/Login';
import Dashboard    from './pages/Dashboard';
import Leads        from './pages/Leads';
import LeadDetail   from './pages/LeadDetail';
import Replies      from './pages/Replies';
import Campaigns    from './pages/Campaigns';
import NewCampaign  from './pages/NewCampaign';
import GenerateLeads from './pages/GenerateLeads';
import Analytics    from './pages/Analytics';
import Settings     from './pages/Settings';
import Contacts     from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import EmailManager from './pages/EmailManager';
import Users        from './pages/Users';
import Drafts        from './pages/Drafts';
import Pipeline      from './pages/Pipeline';

// Safe error message extractor — handles Pydantic v2 array errors
export function safeErr(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(' · ');
  return JSON.stringify(d);
}

// Format checks mirroring the backend's EMAIL_RE / PHONE_CHARS_RE — catch typos
// before a round-trip to the server.
export function isValidEmail(email) {
  return /^(?!.*\.\.)[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email||'').trim());
}
export function isValidPhone(phone) {
  phone = (phone||'').trim();
  if (!phone) return true; // optional field
  const digits = phone.replace(/\D/g,'');
  return /^\+?[0-9\-.\s()]{6,20}$/.test(phone) && digits.length>=7 && digits.length<=15;
}

// Derives the open-tracking label/tooltip shown next to a sent email — shared
// by LeadDetail and EmailManager so the wording stays consistent everywhere.
export function openStatus(openedAt, openCount) {
  if (!openedAt) return { opened: false, label: 'Not opened yet', tooltip: 'Not opened yet' };
  const suffix = openCount > 1 ? ` ×${openCount}` : '';
  return {
    opened:  true,
    label:   `👁 Opened${suffix}`,
    tooltip: `Opened ${new Date(openedAt).toLocaleString('en-IN')}${suffix}`,
  };
}

// Route-level permission guard — the sidebar nav already hides links the user
// can't access, but that doesn't stop direct navigation to the URL. Without
// this, the page shell renders for anyone logged in (backend still blocks the
// actual data, but the broken-looking empty page is its own bug).
function RequireRoute({ perm, children }) {
  const { can } = useAuth();
  if (perm && !can(perm)) return <Navigate to="/" replace/>;
  return children;
}

const NAV = [
  { path:'/',              label:'Dashboard',      icon:'⊞', section:'main',  perm:null          },
  { path:'/leads',         label:'Leads',          icon:'◎', section:'main',  perm:'leads',        badge:'leads'   },
  { path:'/contacts',      label:'Contacts',       icon:'👥', section:'main', perm:'contacts'                      },
  { path:'/pipeline',      label:'Pipeline',       icon:'▦', section:'main',  perm:'pipeline'                      },
  { path:'/replies',       label:'Replies',        icon:'↩', section:'main',  perm:'replies',      badge:'replies' },
  { path:'/email-manager', label:'Email',          icon:'✉', section:'main',  perm:'email',        badge:'email'   },
  { path:'/campaigns',     label:'Campaigns',      icon:'🚀', section:'main', perm:'campaigns'                     },
  { path:'/generate',      label:'Generate leads', icon:'✦', section:'tools', perm:'generate_leads'},
  { path:'/drafts',        label:'AI Drafts',      icon:'📝', section:'tools', perm:'email'                        },
  { path:'/analytics',     label:'Analytics',      icon:'📊', section:'tools',perm:'analytics'                     },
  { path:'/users',         label:'Users',          icon:'👤', section:'tools', perm:'users'                        },
  { path:'/settings',      label:'Settings',       icon:'⚙', section:'tools', perm:'settings'                     },
];

const META = {
  '/':              ['Dashboard',       'Overview of your outreach activity'],
  '/leads':         ['Leads',           'All generated leads and their status'],
  '/contacts':      ['Contacts',        'Your contact book'],
  '/pipeline':      ['Pipeline',         'Interested leads moving toward a deal'],
  '/replies':       ['Replies',         'Lead replies'],
  '/email-manager': ['Email',           'Compose, inbox, sent and reply'],
  '/campaigns':     ['Campaigns',       'Manage your outreach campaigns'],
  '/generate':      ['Generate leads',  'Find new prospects using AI'],
  '/drafts':        ['AI Drafts',       'Review and approve AI-drafted emails before they send'],
  '/analytics':     ['Analytics',       'Performance overview'],
  '/users':         ['Users',           'Manage team members and permissions'],
  '/settings':      ['Settings',        'Configure email, AI and automation'],
};

const ROLE_COLORS = {
  admin:     'var(--blue)',
  manager:   'var(--green)',
  sales_rep: 'var(--amber)',
  read_only: 'var(--purple)',
};

function Toast({ toast, onClose }) {
  if (!toast) return null;
  // Safely convert message to string regardless of type
  const msg = (() => {
    const m = toast.msg;
    if (!m) return '';
    if (typeof m === 'string') return m;
    if (Array.isArray(m)) return m.map(x => x?.msg || JSON.stringify(x)).join(' · ');
    if (typeof m === 'object') return m.msg || m.detail || JSON.stringify(m);
    return String(m);
  })();
  return (
    <div className={`toast ${toast.type==='error'?'toast-error':'toast-success'}`}>
      <span>{msg}</span>
      <button style={{background:'none',border:'none',cursor:'pointer',
        fontSize:14,color:'inherit',marginLeft:4}} onClick={onClose}>✕</button>
    </div>
  );
}

function ChangePasswordModal({ onClose, showToast }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const submit = async () => {
    if (!currentPassword || !newPassword) { setErr('Fill in both fields'); return; }
    if (newPassword.length < 6) { setErr('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setErr('New passwords do not match'); return; }
    setSaving(true); setErr('');
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      showToast('✓ Password changed successfully!');
      onClose();
    } catch (e) {
      setErr(safeErr(e, 'Failed to change password'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{width:420}}>
        <div className="modal-header">
          <span className="modal-title">Change password</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-error">{err}</div>}
          <div className="form-group">
            <label className="form-label">Current password</label>
            <input className="form-input" type="password" value={currentPassword}
              onChange={e=>setCurrentPassword(e.target.value)} autoFocus/>
          </div>
          <div className="form-group">
            <label className="form-label">New password</label>
            <input className="form-input" type="password" value={newPassword}
              onChange={e=>setNewPassword(e.target.value)} placeholder="At least 6 characters"/>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm new password</label>
            <input className="form-input" type="password" value={confirmPassword}
              onChange={e=>setConfirmPassword(e.target.value)}
              onKeyDown={e=>e.key==='Enter' && submit()}/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" disabled={saving} onClick={submit}>
            {saving ? 'Saving…' : 'Change password'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ unreadReplies, unreadEmails, collapsed, setCollapsed, showToast }) {
  const { user, logout, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [hovered, setHovered] = useState(null);
  const [showPwModal, setShowPwModal] = useState(false);
  const isActive = p => p==='/'?loc.pathname==='/':loc.pathname.startsWith(p);

  // Filter nav items by permission
  const visibleNav = NAV.filter(n => !n.perm || can(n.perm));

  return (
    <aside style={{
      width: collapsed?52:200, flexShrink:0,
      background:'var(--surface)', borderRight:'1px solid var(--border)',
      display:'flex', flexDirection:'column',
      transition:'width .22s cubic-bezier(.4,0,.2,1)',
      overflow:'hidden', position:'relative',
    }}>
      {/* Brand */}
      <div style={{
        padding:collapsed?'14px 0':'14px',
        borderBottom:'1px solid var(--border)',
        display:'flex', alignItems:'center',
        justifyContent:collapsed?'center':'space-between',
        flexShrink:0, minHeight:52,
      }}>
        {!collapsed && (
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:28,height:28,background:'var(--blue)',borderRadius:6,
              display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:14}}>✈</div>
            <div style={{fontSize:14,fontWeight:700}}>Sales<span style={{color:'var(--blue)'}}>AI</span></div>
          </div>
        )}
        {collapsed && <div style={{width:28,height:28,background:'var(--blue)',borderRadius:6,
          display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:14}}>✈</div>}
        {!collapsed && (
          <button onClick={()=>setCollapsed(true)} style={{
            background:'none',border:'1px solid var(--border)',borderRadius:6,
            cursor:'pointer',padding:'3px 6px',color:'var(--text-2)',fontSize:13,lineHeight:1,
          }}>◀</button>
        )}
      </div>

      {/* Nav */}
      <nav style={{flex:1,padding:'8px 0',overflowY:'auto',overflowX:'hidden'}}>
        {collapsed && (
          <button onClick={()=>setCollapsed(false)} style={{
            display:'flex',alignItems:'center',justifyContent:'center',
            width:'100%',padding:'8px 0',background:'none',border:'none',
            cursor:'pointer',color:'var(--text-2)',fontSize:16,
            borderBottom:'1px solid var(--border)',marginBottom:4,
          }}>▶</button>
        )}
        {['main','tools'].map(sec => (
          <React.Fragment key={sec}>
            {collapsed
              ? <div style={{height:1,background:'var(--border)',margin:'6px 8px'}}/>
              : <div style={{padding:'10px 12px 3px',fontSize:9,fontWeight:600,color:'var(--text-3)',
                  letterSpacing:'.08em',textTransform:'uppercase'}}>{sec}</div>}
            {visibleNav.filter(n=>n.section===sec).map(n => {
              const active      = isActive(n.path);
              const replyBadge  = n.badge==='replies' && unreadReplies>0;
              const emailBadge  = n.badge==='email'   && unreadEmails>0;
              const hasBadge    = replyBadge||emailBadge;
              const badgeCount  = replyBadge?unreadReplies:unreadEmails;
              return (
                <div key={n.path} style={{position:'relative'}}
                  onMouseEnter={()=>collapsed&&setHovered(n.path)}
                  onMouseLeave={()=>setHovered(null)}>
                  <button onClick={()=>nav(n.path)} style={{
                    display:'flex', alignItems:'center',
                    justifyContent:collapsed?'center':'flex-start',
                    gap:collapsed?0:8,
                    padding:collapsed?'9px 0':'8px 10px',
                    margin:collapsed?'1px 4px':'1px 5px',
                    borderRadius:6, fontSize:13, fontWeight:500,
                    color:active?'var(--blue)':'var(--text-2)',
                    background:active?'var(--blue-bg)':'none',
                    border:'none', cursor:'pointer',
                    width:collapsed?'calc(100% - 8px)':'calc(100% - 10px)',
                  }}>
                    <span style={{fontSize:15,flexShrink:0,width:collapsed?'auto':18,
                      textAlign:'center',position:'relative'}}>
                      {n.icon}
                      {collapsed && hasBadge && (
                        <span style={{position:'absolute',top:-4,right:-6,width:14,height:14,
                          background:'var(--red)',color:'#fff',fontSize:8,fontWeight:700,
                          borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',
                          border:'1.5px solid var(--surface)'}}>
                          {badgeCount>9?'9+':badgeCount}
                        </span>
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span style={{flex:1,textAlign:'left',whiteSpace:'nowrap',
                          overflow:'hidden',textOverflow:'ellipsis'}}>{n.label}</span>
                        {hasBadge && (
                          <span style={{marginLeft:'auto',minWidth:18,padding:'1px 5px',
                            background:'var(--red)',color:'#fff',fontSize:10,fontWeight:700,
                            borderRadius:10,textAlign:'center',lineHeight:'16px'}}>{badgeCount}</span>
                        )}
                      </>
                    )}
                  </button>
                  {/* Tooltip */}
                  {collapsed && hovered===n.path && (
                    <div style={{position:'absolute',left:'calc(100% + 10px)',top:'50%',
                      transform:'translateY(-50%)',background:'#1F2937',color:'#fff',
                      padding:'5px 10px',borderRadius:6,fontSize:12,fontWeight:500,
                      whiteSpace:'nowrap',pointerEvents:'none',zIndex:1000,
                      boxShadow:'0 4px 12px rgba(0,0,0,.25)'}}>
                      {n.label}
                      <div style={{position:'absolute',right:'100%',top:'50%',
                        transform:'translateY(-50%)',border:'5px solid transparent',
                        borderRightColor:'#1F2937'}}/>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </nav>

      {/* Footer — user info + logout */}
      <div style={{
        padding:collapsed?'12px 0':'12px 14px',
        borderTop:'1px solid var(--border)',
        display:'flex',alignItems:'center',
        justifyContent:collapsed?'center':'flex-start',
        gap:10,flexShrink:0,
      }}>
        <div style={{
          width:30,height:30,borderRadius:'50%',flexShrink:0,
          background: ROLE_COLORS[user?.role]||'var(--blue-bg)',
          display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:13,fontWeight:700,color:'#fff',
        }}>
          {user?.name?.charAt(0)?.toUpperCase()||'?'}
        </div>
        {!collapsed && (
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {user?.name}
            </div>
            <div style={{fontSize:10,color:'var(--text-2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:5}}>
              <span style={{color:ROLE_COLORS[user?.role]||'var(--blue)',fontWeight:500}}>
                {user?.role_label}
              </span>
              <button onClick={()=>setShowPwModal(true)} style={{background:'none',border:'none',cursor:'pointer',
                color:'var(--text-3)',fontSize:10,padding:0,marginLeft:'auto'}}>
                Password
              </button>
              <button onClick={logout} style={{background:'none',border:'none',cursor:'pointer',
                color:'var(--text-3)',fontSize:10,padding:0}}>
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
      {showPwModal && <ChangePasswordModal onClose={()=>setShowPwModal(false)} showToast={showToast}/>}
    </aside>
  );
}

function Topbar({ collapsed, setCollapsed }) {
  const location = useLocation();
  const nav      = useNavigate();
  const { user, logout } = useAuth();
  const base = '/' + location.pathname.split('/')[1];
  const [title, sub] = META[base]||['',''];
  const isDetail = location.pathname.split('/').length>2;

  return (
    <div className="topbar">
      <div className="topbar-left">
        <button onClick={()=>setCollapsed(!collapsed)} style={{
          background:'none',border:'1px solid var(--border)',borderRadius:6,
          cursor:'pointer',padding:'5px 8px',color:'var(--text-2)',fontSize:14,
          lineHeight:1,flexShrink:0,marginRight:4,
        }}>{collapsed?'☰':'✕'}</button>
        {isDetail && <button className="btn btn-sm" onClick={()=>nav(-1)}>← Back</button>}
        <div>
          <div className="page-title">{title}</div>
          {sub && <div className="page-sub">{sub}</div>}
        </div>
      </div>
      <div className="topbar-actions">
        {base==='/'        && <><button className="btn btn-sm" onClick={()=>nav('/generate')}>✦ Generate</button><button className="btn btn-primary btn-sm" onClick={()=>nav('/campaigns/new')}>+ Campaign</button></>}
        {base==='/leads'   && <button className="btn btn-primary btn-sm" onClick={()=>nav('/generate')}>✦ Generate leads</button>}
        {base==='/campaigns' && <button className="btn btn-primary btn-sm" onClick={()=>nav('/campaigns/new')}>+ New campaign</button>}
      </div>
    </div>
  );
}

function AppInner() {
  const { user, loading } = useAuth();
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [unreadEmails,  setUnreadEmails]  = useState(0);
  const [toast,         setToast]         = useState(null);
  const [collapsed,     setCollapsed]     = useState(false);

  const showToast = (msg, type='success') => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  };

  useEffect(()=>{
    if (!user) return;
    const poll = () => {
      authApi.get('/replies/unread-count').then(r=>setUnreadReplies(r.data.count)).catch(()=>{});
      authApi.get('/emails/counts').then(r=>setUnreadEmails(r.data.unread||0)).catch(()=>{});
    };
    poll();
    let ws = null;
    const connectWS = () => {
      try {
        ws = new WebSocket(WS_URL);
        ws.onmessage = e => {
          try {
            const d = JSON.parse(e.data);
            if (d.type==='new_replies') { setUnreadReplies(p=>p+d.count); setUnreadEmails(p=>p+d.count); poll(); }
          } catch(err) {}
        };
        ws.onclose = () => setTimeout(connectWS, 3000);
        ws.onerror = () => ws.close();
      } catch(e) {}
    };
    connectWS();
    const t = setInterval(poll, 30000);
    return () => { clearInterval(t); if(ws) ws.close(); };
  }, [user]);

  if (loading) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)'}}>
      <div className="spinner spinner-lg"/>
    </div>
  );

  if (!user) return <Login onLogin={(u) => {
    // Auth context reads from localStorage which Login.js just set
    window.location.reload();
  }}/>;

  return (
    <div className="app-shell">
      <Sidebar unreadReplies={unreadReplies} unreadEmails={unreadEmails}
        collapsed={collapsed} setCollapsed={setCollapsed} showToast={showToast}/>
      <div className="main-area">
        <Topbar collapsed={collapsed} setCollapsed={setCollapsed}/>
        <div className="page-content">
          <Toast toast={toast} onClose={()=>setToast(null)}/>
          <Routes>
            <Route path="/"                element={<Dashboard    showToast={showToast}/>}/>
            <Route path="/leads"           element={<RequireRoute perm="leads"><Leads        showToast={showToast}/></RequireRoute>}/>
            <Route path="/leads/:id"       element={<RequireRoute perm="leads"><LeadDetail   showToast={showToast}/></RequireRoute>}/>
            <Route path="/contacts"        element={<RequireRoute perm="contacts"><Contacts     showToast={showToast}/></RequireRoute>}/>
            <Route path="/contacts/:id"    element={<RequireRoute perm="contacts"><ContactDetail showToast={showToast}/></RequireRoute>}/>
            <Route path="/pipeline"        element={<RequireRoute perm="pipeline"><Pipeline    showToast={showToast}/></RequireRoute>}/>
            <Route path="/replies"         element={<RequireRoute perm="replies"><Replies      showToast={showToast} onRead={()=>setUnreadReplies(p=>Math.max(0,p-1))}/></RequireRoute>}/>
            <Route path="/email-manager"   element={<RequireRoute perm="email"><EmailManager showToast={showToast} onCountChange={setUnreadEmails}/></RequireRoute>}/>
            <Route path="/campaigns"       element={<RequireRoute perm="campaigns"><Campaigns    showToast={showToast}/></RequireRoute>}/>
            <Route path="/campaigns/new"   element={<RequireRoute perm="campaigns"><NewCampaign  showToast={showToast}/></RequireRoute>}/>
            <Route path="/generate"        element={<RequireRoute perm="generate_leads"><GenerateLeads showToast={showToast}/></RequireRoute>}/>
            <Route path="/drafts"          element={<RequireRoute perm="email"><Drafts        showToast={showToast}/></RequireRoute>}/>
            <Route path="/analytics"       element={<RequireRoute perm="analytics"><Analytics/></RequireRoute>}/>
            <Route path="/users"           element={<RequireRoute perm="users"><Users        showToast={showToast}/></RequireRoute>}/>
            <Route path="/settings"        element={<RequireRoute perm="settings"><Settings     showToast={showToast}/></RequireRoute>}/>
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppInner/>
      </AuthProvider>
    </BrowserRouter>
  );
}
